import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { Loader2, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";

interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

interface FAQCategory {
  id: string;
  name: string;
  display_order: number;
  questions: FAQQuestion[];
}

const FAQ = () => {
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [allQuestions, setAllQuestions] = useState<{ question: string; answer: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFAQData();
  }, []);

  const loadFAQData = async () => {
    try {
      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("faq_categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (categoriesError) throw categoriesError;

      // Fetch questions
      const { data: questionsData, error: questionsError } = await supabase
        .from("faq_questions")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (questionsError) throw questionsError;

      // Group questions by category
      const categoriesWithQuestions = (categoriesData || []).map((category) => ({
        ...category,
        questions: (questionsData || []).filter(
          (q) => q.category_id === category.id
        ),
      }));

      setCategories(categoriesWithQuestions);
      
      // Flatten all questions for FAQ Schema
      const allFAQs = (questionsData || []).map((q) => ({
        question: q.question,
        answer: q.answer,
      }));
      setAllQuestions(allFAQs);
    } catch (error) {
      console.error("Error loading FAQ data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageTransition>
      <SEO
        title="שאלות נפוצות"
        description="שאלות נפוצות על אליס תכשיטים - תשובות לשאלות על רכישה, משלוחים, החזרות, אחריות ועוד. מצאו את כל התשובות במקום אחד"
        canonical="https://alice.jewelry/faq"
        breadcrumbs={[
          { name: 'דף הבית', url: '/' },
          { name: 'שאלות נפוצות', url: '/faq' },
        ]}
        faqItems={allQuestions.length > 0 ? allQuestions : undefined}
      />
      <div className="min-h-screen flex flex-col" dir="rtl">
        <Header />

        <main className="flex-1 pt-32 pb-16 px-4 md:px-8 lg:px-16">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-primary/10 rounded-full">
                  <HelpCircle className="h-10 w-10 text-primary" />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4">
                שאלות נפוצות
              </h1>
              <p className="text-muted-foreground text-lg">
                מצאו תשובות לשאלות הנפוצות ביותר
              </p>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {/* FAQ Categories */}
            {!isLoading && categories.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                אין שאלות נפוצות להצגה כרגע
              </div>
            )}

            {!isLoading && categories.length > 0 && (
              <div className="space-y-8">
                {categories.map((category) => (
                  <div key={category.id} className="bg-card rounded-lg border border-border p-6">
                    <h2 className="text-xl font-medium text-foreground mb-4 pb-3 border-b border-border">
                      {category.name}
                    </h2>
                    
                    {category.questions.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        אין שאלות בקטגוריה זו
                      </p>
                    ) : (
                      <Accordion type="single" collapsible className="w-full">
                        {category.questions.map((question) => (
                          <AccordionItem
                            key={question.id}
                            value={question.id}
                            className="border-b border-border/50 last:border-b-0"
                          >
                            <AccordionTrigger className="text-right hover:no-underline py-4">
                              <span className="text-foreground font-normal">
                                {question.question}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground pb-4 whitespace-pre-line leading-relaxed">
                              {question.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Contact CTA */}
            <div className="mt-12 text-center p-8 bg-muted/30 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-2">
                לא מצאתם תשובה לשאלה שלכם?
              </h3>
              <p className="text-muted-foreground mb-4">
                צרו איתנו קשר ונשמח לעזור
              </p>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                צור קשר
              </a>
            </div>
          </div>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default FAQ;
